import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { COMPANY } from "@/config/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const units = ["MT (Metric Tonnes)", "KG", "20ft Container", "40ft Container", "Bags", "Litres"];
const paymentTermsOptions = [
  "Advance TT", "LC at Sight", "LC 30 Days", "LC 60 Days", "LC 90 Days",
  "DP / DA", "Partial Advance + Balance", "To be discussed",
];

const empty = {
  name: "", company: "", email: "", phone: "",
  product_name: "", quantity: "", unit: "", destination_port: "",
  packaging_type: "", payment_terms: "", delivery_date: "", message: "",
};

export default function RFQ() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.product_name) {
      toast.error("Please fill your name, email and the product name.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, source: "rfq" });
      toast.success("Quote request received. Our trade desk will respond within 24 hours.");
      setForm(empty);
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.message || "Please try again.";
      toast.error(`Could not submit: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="rfq" data-testid="rfq-section" className="relative py-24 md:py-32 bg-[#1A3626]">
      <div className="absolute inset-0 grain"></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="overline text-[#E6C97A]">Request for Quote</p>
            <h2 className="mt-4 font-serif-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
              Start a shipment<br />
              <span className="text-[#E6C97A] italic">conversation.</span>
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-md">
              Share your requirement — product, grade, quantity and destination — and our
              trade desk will revert with indicative pricing and lead time within one
              business day.
            </p>

            <div className="mt-10 space-y-4 text-white/80 text-sm">
              <div><span className="overline text-[#C5A059] block mb-1">Direct line / WhatsApp</span>
                <a href={`tel:+${COMPANY.phoneRaw}`} className="hover:text-[#E6C97A]">{COMPANY.phone}</a>
              </div>
              <div><span className="overline text-[#C5A059] block mb-1">Trade desk</span>
                <a href={`mailto:${COMPANY.email}`} className="hover:text-[#E6C97A]">{COMPANY.email}</a>
              </div>
              <div><span className="overline text-[#C5A059] block mb-1">Head office</span>{COMPANY.headOffice}</div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            className="lg:col-span-7 bg-white p-8 md:p-12"
            data-testid="rfq-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name *">
                <Input data-testid="rfq-input-name" value={form.name} onChange={set("name")} placeholder="Your name" className={inp} />
              </Field>
              <Field label="Company">
                <Input data-testid="rfq-input-company" value={form.company} onChange={set("company")} placeholder="Company name" className={inp} />
              </Field>
              <Field label="Email *">
                <Input data-testid="rfq-input-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inp} />
              </Field>
              <Field label="Phone / WhatsApp">
                <Input data-testid="rfq-input-phone" value={form.phone} onChange={set("phone")} placeholder="+00 000 000 000" className={inp} />
              </Field>
              <Field label="Product Name *" full>
                <Input data-testid="rfq-input-product" value={form.product_name} onChange={set("product_name")} placeholder="e.g. Basmati Rice 1121, Turmeric Finger, Groundnut Oil" className={inp} />
              </Field>
              <Field label="Quantity">
                <Input data-testid="rfq-input-quantity" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 25" className={inp} />
              </Field>
              <Field label="Unit">
                <Select value={form.unit} onValueChange={(v) => setForm((f) => ({ ...f, unit: v }))}>
                  <SelectTrigger data-testid="rfq-input-unit" className={inp + " focus:ring-[#C5A059]"}>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Destination Port / Country">
                <Input data-testid="rfq-input-destination" value={form.destination_port} onChange={set("destination_port")} placeholder="e.g. Jebel Ali, UAE" className={inp} />
              </Field>
              <Field label="Packaging Type">
                <Input data-testid="rfq-input-packaging" value={form.packaging_type} onChange={set("packaging_type")} placeholder="e.g. PP bags 25kg, vacuum, bulk" className={inp} />
              </Field>
              <Field label="Preferred Payment Terms">
                <Select value={form.payment_terms} onValueChange={(v) => setForm((f) => ({ ...f, payment_terms: v }))}>
                  <SelectTrigger data-testid="rfq-input-payment" className={inp + " focus:ring-[#C5A059]"}>
                    <SelectValue placeholder="Select terms" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentTermsOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Required Delivery Date">
                <Input data-testid="rfq-input-delivery-date" type="date" value={form.delivery_date} onChange={set("delivery_date")} className={inp} />
              </Field>
              <Field label="Additional Notes" full>
                <Textarea data-testid="rfq-input-message" value={form.message} onChange={set("message")} rows={3} placeholder="Grade, Incoterm, target price, certifications..." className={inp} />
              </Field>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="rfq-form-submit"
              className="mt-8 btn-primary bg-[#1A3626] hover:bg-[#244A35] w-full md:w-auto disabled:opacity-60"
            >
              {loading ? "Submitting..." : "Submit Request"}
              <Send size={16} strokeWidth={1.5} />
            </button>
            <p className="mt-4 text-xs text-[#4B5563]">
              By submitting, you agree to be contacted by our trade desk. We never share your details.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

const inp = "rounded-none border-[#1A3626]/25 focus-visible:ring-[#C5A059]";

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-widest text-[#1A3626]/70 mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
